import * as React from "react";
import { useEffect, useRef } from "react";
import { useAuth } from "../auth/AuthProvider";
import SubscriptionPanel from "./SubscriptionPanel";

type SidebarProps = {
  open: boolean;
  onToggle: () => void;
};

function initialsOf(name?: string, email?: string) {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return src.slice(0, 2).toUpperCase();
}

export default function Sidebar({ open, onToggle }: SidebarProps) {
  const { user, logout } = useAuth();
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on Escape when expanded
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onToggle();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onToggle]);

  return (
    <>
      {/* Backdrop (only visible when expanded, sits below the drawer) */}
      <div
        className={`fixed inset-0 z-30 bg-black/30 transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onToggle}
        aria-hidden={!open}
      />

      {/* Drawer / rail */}
      <aside
        ref={ref}
        className={`fixed top-0 left-0 z-40 h-full bg-white border-r border-gray-200 shadow-sm transition-[width] duration-200 ease-out flex flex-col ${
          open ? "w-64" : "w-12"
        }`}
        aria-label="Account menu"
      >
        {/* Toggle / brand row */}
        <div className="h-12 flex items-center border-b border-gray-200 px-2">
          <button
            type="button"
            onClick={onToggle}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
            aria-expanded={open}
          >
            <svg
              viewBox="0 0 24 24"
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          {open && (
            <span className="ml-2 text-sm font-semibold text-gray-900 tracking-tight">
              ESI Flow
            </span>
          )}
        </div>

        {/* Profile block */}
        <div className="p-2">
          <div
            className={`flex items-center ${
              open ? "gap-3 p-2" : "justify-center p-1"
            } rounded-md ${open ? "bg-gray-50" : ""}`}
          >
            <span
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-500 text-white text-xs font-semibold flex-shrink-0"
              title={user?.name || user?.email || "Not signed in"}
            >
              {user ? initialsOf(user.name, user.email) : "?"}
            </span>
            {open && (
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || "Not signed in"}
                </p>
                {user?.email && (
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Subscription management (only in the expanded drawer) */}
        {open && user && <SubscriptionPanel />}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Sign out */}
        {user && (
          <div className="border-t border-gray-200 p-2">
            <button
              type="button"
              onClick={() => {
                logout();
                onToggle();
              }}
              className={`flex items-center ${
                open ? "gap-2 px-3 py-2 w-full text-left" : "justify-center w-8 h-8 mx-auto"
              } rounded-md text-sm text-gray-700 hover:bg-gray-100 transition-colors`}
              aria-label="Sign out"
              title="Sign out"
            >
              <svg
                viewBox="0 0 24 24"
                className="w-4 h-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              {open && <span>Sign out</span>}
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
